// AttendanceForm.js (Bulk Entry Mode)
import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Card, Container, Row, Col, Table, Spinner } from 'react-bootstrap';
import { FaUserCheck, FaUserTimes, FaSave } from 'react-icons/fa';
import api from '../../services/api';
import Layout from '../../components/Layout';
import TopBar from '../../components/TopBar';
import ToastNotification from '../../components/ToastNotification';

const AttendanceForm = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [date, setDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [formErrors, setFormErrors] = useState([]);
  const [leaves, setLeaves] = useState([]);
  
  // Create ref to track initial render
  const isInitialDateRender = useRef(true);

  // Get leave status for all employees
  const getEmployeeLeaveStatus = (employeeId) => {
    const employeeLeave = leaves.find(leave => leave.employee_id === employeeId);
    if (employeeLeave) {
      return {
        hasLeave: true,
        status: employeeLeave.status,
        type: employeeLeave.leave_type?.name || 'Leave'
      };
    }
    return { hasLeave: false };
  };

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Set default date to current date
        const today = new Date().toISOString().split('T')[0];
        setDate(today);
        
        // Fetch departments
        const deptResponse = await api.get('/departments');
        setDepartments(deptResponse.data);
        
        // Fetch active employees with their department
        const empResponse = await api.get('/employees?with=department&status=active');
        const employeesData = empResponse.data
          .filter(emp => emp.status === 'active') // Double-check status on client side
          .map(emp => ({
            ...emp,
            status: '',
            hours_worked: '',
            overtime_hours: '',
            hasLeave: false,
            leaveStatus: '',
            leaveType: ''
          }));
        
        // Store initial employee data
        setEmployees(employeesData);
        setFilteredEmployees(employeesData);
        
        // Fetch leaves for today
        const leavesResponse = await api.get(`/leaves/day?date=${today}`);
        const leavesData = leavesResponse.data;
        setLeaves(leavesData);
        
        // Create a map of leaves for quick lookup
        const leavesMap = new Map();
        leavesData.forEach(leave => {
          // Store all leave statuses
          leavesMap.set(leave.employee_id, {
            status: leave.status,
            type: leave.leave_type?.name || leave.leave_type_name || 'Leave',
            leave_id: leave.id,
            start_date: leave.start_date,
            end_date: leave.end_date
          });
        });
        
        // Update employees with leave information
        const updatedEmployees = employeesData.map(emp => {
          const leaveInfo = leavesMap.get(emp.user_id) || { status: '', type: '' };
          // If leave is approved, set status to 'leave'
          const status = (leaveInfo.status === 'approved') ? 'leave' : emp.status;
          return {
            ...emp,
            hasLeave: !!leaveInfo,
            leaveStatus: leaveInfo.status,
            leaveType: leaveInfo.type,
            leaveId: leaveInfo.leave_id,
            leaveStartDate: leaveInfo.start_date,
            leaveEndDate: leaveInfo.end_date,
            status
          };
        });
        
        setEmployees(updatedEmployees);
        setFilteredEmployees(updatedEmployees);
        
        // Now fetch attendance for today
        const attendanceResponse = await api.get(`/attendances?date=${today}`);
        const attendanceData = attendanceResponse.data;
        
        // Update employee data with attendance information and leave status
        if (attendanceData && attendanceData.length > 0) {
          const updatedEmployees = employeesData.map(emp => {
            const existingAttendance = attendanceData.find(a => a.employee_id === emp.user_id);
            const leaveInfo = leavesMap.get(emp.user_id) || { status: '', type: '' };
            // If leave is approved, set status to 'leave'
            const status = (leaveInfo.status === 'approved') ? 'leave' : (existingAttendance?.status || (leaveInfo ? 'leave' : ''));
            return {
              ...emp,
              status,
              hours_worked: existingAttendance?.hours_worked || '',
              overtime_hours: existingAttendance?.overtime_hours || '',
              hasLeave: !!leaveInfo,
              leaveStatus: leaveInfo.status,
              leaveType: leaveInfo.type
            };
          });
          
          setEmployees(updatedEmployees);
          setFilteredEmployees(updatedEmployees);
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setToastMessage('Error loading attendance data');
        setToastVariant('danger');
        setShowToast(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeData();
  }, []);
  
  // Fetch attendance data when date changes (but not on initial load)
  useEffect(() => {
    // Skip the first render when date is set by the initialization
    if (isInitialDateRender.current) {
      isInitialDateRender.current = false;
      return;
    }
    
    if (date) {
      setIsLoading(true);
      
      // Fetch both attendance and leaves data for the selected date
      Promise.all([
        api.get(`/attendances?date=${date}`),
        api.get(`/leaves/day?date=${date}`)
      ])
        .then(([attendanceRes, leavesRes]) => {
          const attendanceData = attendanceRes.data;
          const leavesData = leavesRes.data;
          
          // Create a map of leaves for quick lookup
          const leavesMap = new Map();
          leavesData.forEach(leave => {
            if (leave.status === 'approved' || leave.status === 'pending') {
              leavesMap.set(leave.employee_id, {
                status: leave.status,
                type: leave.leave_type?.name || 'Leave'
              });
            }
          });

          
          
          setEmployees(prevEmployees => {
            const updatedEmployees = prevEmployees.map(emp => {
              const existingAttendance = attendanceData.find(a => a.employee_id === emp.user_id);
              const leaveInfo = leavesMap.get(emp.user_id) || { status: '', type: '' };
              // If leave is approved, set status to 'leave'
              const status = (leaveInfo.status === 'approved') ? 'leave' : (existingAttendance?.status || (leaveInfo ? 'leave' : ''));
              return {
                ...emp,
                status,
                hours_worked: existingAttendance?.hours_worked || '',
                overtime_hours: existingAttendance?.overtime_hours || '',
                hasLeave: !!leaveInfo,
                leaveStatus: leaveInfo.status,
                leaveType: leaveInfo.type
              };
            });
            return updatedEmployees;
          });
          setLeaves(leavesData);
        })
        .catch(error => {
          console.error('Error fetching attendance:', error);
          setToastMessage('Error loading attendance data for selected date');
          setToastVariant('danger');
          setShowToast(true);
        })
        .finally(() => setIsLoading(false));
    }
  }, [date]);

  // Filter employees when department changes
  useEffect(() => {
    setIsLoading(true);
    if (selectedDepartment) {
      const filtered = employees.filter(
        emp => emp.department_id && emp.department_id.toString() === selectedDepartment
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(employees);
    }
    setIsLoading(false);
  }, [selectedDepartment, employees]);

  const markAll = (status) => {
    const updatedEmployees = employees.map(emp => {
      // Skip employees with approved leaves
      if (emp.hasLeave && emp.leaveStatus === 'approved') {
        return emp;
      }
      return { ...emp, status };
    });
    setEmployees(updatedEmployees);
    setFilteredEmployees(updatedEmployees);
  }

  const handleChange = (index, field, value) => {
    const updated = [...employees];
    const employee = updated[index];
    
    // Prevent status change for employees with approved leaves
    if (field === 'status' && employee.hasLeave && employee.leaveStatus === 'approved') {
      return;
    }
    
    updated[index][field] = value;
    setEmployees(updated);
    setFilteredEmployees(updated);
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = [];
    const incompleteEmployees = employees.filter(emp => !emp.status);
    
    if (incompleteEmployees.length > 0) {
      errors.push(`${incompleteEmployees.length} employee(s) don't have a status selected`);
    }
    
    if (!date) {
      errors.push('Please select a date');
    }
    
    setFormErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setToastMessage('Please fix the form errors before submitting');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }
    
    const payload = employees.map(emp => ({
      employee_id: emp.user_id,
      date,
      status: emp.status || '',
      hours_worked: emp.hours_worked || '',
      overtime_hours: emp.overtime_hours || ''
    }));
    
    setIsLoading(true);
    api.post('/attendances/bulk', payload)
      .then(() => {
        setToastMessage('Bulk attendance saved successfully!');
        setToastVariant('success');
        setShowToast(true);
        setFormErrors([]);
      })
      .catch(error => {
        console.error('Error saving attendance:', error);
        const errorMessage = error.response?.data?.message || 'Error saving attendance data';
        setToastMessage(errorMessage);
        setToastVariant('danger');
        setShowToast(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Layout>
        <TopBar/>
        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          variant={toastVariant}
          delay={3000}
          position="top-end"
        />
        
        {formErrors.length > 0 && (
          <Container fluid className="mt-2">
            <Card className="border-warning mb-3">
              <Card.Header className="bg-warning text-dark">Please fix the following errors:</Card.Header>
              <Card.Body>
                <ul className="mb-0">
                  {formErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          </Container>
        )}
        <Form onSubmit={handleSubmit}>

       <Container fluid className="mt-2">
            <Card className="mb-3 shadow-sm">
              <Card.Body>
                <Row className="mb-0">
                  <Col md={4}>
                    {isLoading && (
                      <div className="position-absolute top-50 start-50 translate-middle bg-gray-200 p-3 rounded shadow-sm" style={{ zIndex: 1050 }}>
                        <div className="d-flex flex-column align-items-center">
                          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        </div>
                      </div>
                    )}
                    <Form.Label>Date</Form.Label>
                    <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Department</Form.Label>
                    <Form.Select
                      value={selectedDepartment}
                      onChange={(e) => setSelectedDepartment(e.target.value)}
                    >
                      <option value="">All Departments</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4} xs={12} className="mt-2 d-flex justify-content-end align-items-center">
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        className="d-flex align-items-center px-2"
                        onClick={() => markAll('present')}
                      >
                        <FaUserCheck className="me-1" />
                        <span className="d-none d-sm-inline">Present</span>
                      </Button>
                      
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        className="d-flex align-items-center px-2"
                        onClick={() => markAll('leave')}
                      >
                        <FaUserTimes className="me-1" />
                        <span className="d-none d-sm-inline">Absent</span>
                      </Button>
                      
                      <Button 
                        type="submit" 
                        variant="primary" 
                        size="sm"
                        className="d-flex align-items-center px-2"
                      >
                        <FaSave className="me-1" />
                        <span className="d-none d-sm-inline">Save</span>
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
       </Container> 

        <Container fluid className="mt-2" style={{ maxHeight: '70vh', overflowY: 'auto' }}>

            <Table striped bordered hover responsive className="table-striped table-hover">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Name</th>
                  <th>Status</th>
             
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp, index) => (
                  <tr key={emp.id}>
                    <td>{index + 1}</td>
                    <td>
                      {emp.name}
                      {emp.hasLeave &&emp.leaveType && (
                        <span className="ms-2 badge bg-secondary">
                          {emp.leaveType} ({emp.leaveStatus})
                        </span>
                      )}
                    </td>
                    <td className={emp.hasLeave ? `table-${emp.leaveStatus === 'approved' ? '' : ''}` : ''}>
                      <div className="d-flex justify-content-around align-items-center" style={{ gap: '10px' }}>
                        <Form.Check
                          type="radio"
                          label={<span><i className="fas fa-check-circle text-success"></i> Present</span>}
                          name={`status-${index}`}
                          value="present"
                          checked={emp.status === 'present'}
                          onChange={(e) => handleChange(index, 'status', e.target.value)}
                          className="custom-radio"
                          disabled={emp.hasLeave && emp.leaveStatus === 'approved'}
                        />
                        {/* <Form.Check
                          type="radio"
                          label={<span><i className="fas fa-sun text-warning"></i> Morning Half</span>}
                          name={`status-${index}`}
                          value="morning_half"
                          checked={emp.status === 'morning_half'}
                          onChange={(e) => handleChange(index, 'status', e.target.value)}
                          className="custom-radio"
                          disabled={emp.hasLeave && emp.leaveStatus === 'approved'}
                        />
                        <Form.Check
                          type="radio"
                          label={<span><i className="fas fa-cloud-sun text-primary"></i> Afternoon Half</span>}
                          name={`status-${index}`}
                          value="afternoon_half"
                          checked={emp.status === 'afternoon_half'}
                          onChange={(e) => handleChange(index, 'status', e.target.value)}
                          className="custom-radio"
                          disabled={emp.hasLeave && emp.leaveStatus === 'approved'}
                        /> */}
                        <Form.Check
                          type="radio"
                          label={<span><i className="fas fa-times-circle text-danger"></i> Leave</span>}
                          name={`status-${index}`}
                          value="leave"
                          checked={emp.hasLeave && emp.leaveStatus === 'approved'|| emp.status === 'leave'}
                          onChange={(e) => handleChange(index, 'status', e.target.value)}
                          className="custom-radio"
                          disabled={emp.hasLeave && emp.leaveStatus === 'approved'}
                        />
                      </div>
                    </td>
              
                 
                  </tr>
                ))}
              </tbody>
            </Table>
         
          
       
        
        </Container>  
         </Form>
    </Layout>
  );
};

export default AttendanceForm;
